import { useMemo, useState, useCallback } from 'react'
import { Sparkles } from 'lucide-react'
import type { ToolRendererProps } from '@/config/types'
import { AgentToolCallStatus } from '@/runtime/types'
import { useAgentRuntime } from '@/context/AgentProvider'
import { FormApplyRequestedEventType, type FormApplyRequestedDetail, type ProposedFormFieldValue } from '@/events/formApplyEvents'
import { TimeAgo } from '@/components/ui/TimeAgo'
import { FormFieldRow } from './FormFieldRow'
import { useTranslations } from '@/hooks/useTranslations'
import { useRuntimeEventEmitter } from '@/hooks'
import { coerceProposal } from './helpers'

export const FormProposalToolRenderer = ({
  call,
  chatId,
  applyAllButtonText,
  showStatus = true
}: ToolRendererProps) => {
  const runtime = useAgentRuntime()
  const emitFormApplyRequested = useRuntimeEventEmitter<FormApplyRequestedDetail>(FormApplyRequestedEventType)
  const t = useTranslations()
  const proposal = useMemo(() => coerceProposal(call), [call])
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set())

  const lastAction = useMemo(() => {
    const out = (call.output ?? {}) as any
    const mp = out?._mp
    const last = mp?.lastAction
    const action = typeof last?.action === 'string' ? last.action : undefined
    const at = typeof last?.at === 'string' ? last.at : undefined
    return { action, at }
  }, [call.output])

  const isApplied = useMemo(() => lastAction.action === 'apply', [lastAction.action])
  const isRejected = useMemo(() => lastAction.action === 'reject', [lastAction.action])

  const canApply = useMemo(
    () =>
      call.status === AgentToolCallStatus.Succeeded &&
      Boolean(proposal.formId) &&
      proposal.fields.length > 0 &&
      !isApplied,
    [call.status, proposal.formId, proposal.fields.length, isApplied]
  )

  const canReject = useMemo(
    () =>
      call.status === AgentToolCallStatus.Succeeded &&
      Boolean(proposal.formId) &&
      proposal.fields.length > 0 &&
      !isApplied &&
      !isRejected,
    [call.status, proposal.formId, proposal.fields.length, isApplied, isRejected]
  )

  const persistToolCallAction = useCallback(
    async (params: { action: 'apply' | 'reject'; fields: ProposedFormFieldValue[] }) => {
      if (!proposal.formId) return
      if (!runtime.transport.recordToolCallAction) return

      await runtime.transport.recordToolCallAction(
        chatId,
        {
          toolCallId: call.id,
          action: params.action,
          data: {
            formId: proposal.formId,
            formName: proposal.formName,
            fields: params.fields
          },
          systemMessage:
            params.action === 'apply'
              ? `Applied ${params.fields.length} field(s) to ${proposal.formName ?? proposal.formId}.`
              : `Rejected ${proposal.formName ?? proposal.formId} suggestion.`
        },
        // Do NOT exclude this client; otherwise the UI won't see the updated tool call in real time.
        { excludeSocketId: false }
      )
    },
    [proposal.formId, proposal.formName, chatId, call.id, runtime.transport]
  )

  const emitApplyRequested = useCallback(
    async (fields: ProposedFormFieldValue[]) => {
      if (!proposal.formId) return
      emitFormApplyRequested({
        fields,
        chatId,
        toolCallId: call.id,
        formId: proposal.formId,
        ...(proposal.formName ? { formName: proposal.formName } : {}),
      })
      try {
        await persistToolCallAction({ action: 'apply', fields })
      } catch {
        return
      }
    },
    [proposal.formId, proposal.formName, chatId, call.id, emitFormApplyRequested, persistToolCallAction]
  )

  const rejectProposal = useCallback(async () => {
    if (!proposal.formId) return
    try {
      await persistToolCallAction({ action: 'reject', fields: proposal.fields })
    } catch {
      return
    }
  }, [proposal.formId, proposal.fields, persistToolCallAction])

  const toggleFieldExpanded = useCallback((fieldName: string) => {
    setExpandedFields((prev) => {
      // Optimize: only create new Set if state actually changes
      if (prev.has(fieldName)) {
        if (prev.size === 1) return new Set()
        const next = new Set(prev)
        next.delete(fieldName)
        return next
      } else {
        const next = new Set(prev)
        next.add(fieldName)
        return next
      }
    })
  }, [])

  const handleFieldApply = useCallback(
    (field: ProposedFormFieldValue) => {
      void emitApplyRequested([field])
    },
    [emitApplyRequested]
  )

  const handleApplyAll = useCallback(() => {
    void emitApplyRequested(proposal.fields)
  }, [emitApplyRequested, proposal.fields])

  const headerText = proposal.formName
    ? `${t.formProposalHeaderPrefix} ${proposal.formName}`
    : `${t.formProposalHeaderPrefix} ${proposal.formId}`

  if (call.status === AgentToolCallStatus.Failed && call.error) {
    return (
      <div className='mp-agent-tool-body'>
        <div className='mp-agent-tool-simple-message'>{t.formProposalFailed}</div>
        {/* <div className='mp-agent-tool-error'>{call.error}</div> */}
      </div>
    )
  }

  if (call.status !== AgentToolCallStatus.Succeeded) {
    return <div className='mp-agent-tool-simple-message'>{t.formProposalPreparing}</div>
  }

  if (!proposal.formId || proposal.fields.length === 0) {
    return <div className='mp-agent-tool-simple-message'>{t.formProposalNone}</div>
  }

  return (
    <div className='mp-agent-tool-body'>
      <div className='mp-agent-tool-simple-message'>{headerText}</div>

      {proposal.notes ? <div className='mp-agent-tool-simple-message'>{proposal.notes}</div> : null}

      <div className='mp-agent-form-proposal-fields'>
        {proposal.fields.map((field) => (
          <FormFieldRow
            key={field.name}
            field={field}
            isExpanded={expandedFields.has(field.name)}
            canApply={canApply}
            onToggleExpand={toggleFieldExpanded}
            onApply={handleFieldApply}
          />
        ))}
      </div>

      <div className='mp-agent-form-proposal-actions'>
        {showStatus && (isApplied || isRejected) && (
          <div className='mp-agent-form-proposal-status'>
            <span className={`mp-agent-form-proposal-badge ${isApplied ? 'is-applied' : 'is-rejected'}`}>
              {isApplied ? t.formProposalApplied : t.formProposalRejected}
            </span>
            {lastAction.at ? (
              <span className='mp-agent-form-proposal-status-time'>
                <TimeAgo date={lastAction.at} />
              </span>
            ) : null}
          </div>
        )}
        {/* Temporary showing apply all button and disabling reject button */}
        <button type='button' onClick={handleApplyAll} className='mp-agent-form-proposal-apply-all'>
          <Sparkles size={14} /> {applyAllButtonText || t.formProposalApplyAll}
        </button>
        {/* {!canReject && !canApply ? null : (
          <>
            <button
              type='button'
              disabled={!canReject}
              onClick={rejectProposal}
              className='mp-agent-form-proposal-reject'
            >
              Reject
            </button>
            <button
              type='button'
              disabled={!canApply}
              onClick={handleApplyAll}
              className='mp-agent-form-proposal-apply-all'
            >
              Apply all
            </button>
          </>
        )} */}
      </div>
    </div>
  )
}
